import { useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { AirportConfig } from "@yb/core";
import { api, type SecurityDay, type SecurityHistory, type ShowUpCurve, type WaitHistory } from "../lib/api";
import { bandLabels, heatColor, leadWords, securitySentence, shapeCurve, shapeHeat, waitWords } from "../lib/history";
import { AirlineMark } from "./AirlineMark";
import { HowToRead } from "./HowToRead";
import { Section } from "./Section";

/**
 * The History page's Security tab: the checkpoint looking back, from BPSS2's boarding-pass
 * scans. When it was busy, by day and clock hour; how long before their flight passengers
 * cleared; and whose passengers come earliest. Every figure is a count of distinct boarding
 * passes the app kept as each day ran; nothing is a schedule times a load factor, and the
 * lanes-open figure BPSS2 reports is not used, because YLW's operations people say it does
 * not reflect what is open.
 */

const card = "rounded-xl border border-line p-4";
const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1.5 align-middle";
const num = (v: number | null | undefined) => (v == null ? "—" : v.toLocaleString());
const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;

export function SecurityTab({ from, to, days, tz, today, config }: { from: string; to: string; days: number; tz: string; today: string; config: AirportConfig | undefined }) {
  const q = useQuery({ queryKey: ["security-history", from, to], queryFn: () => api.securityHistory(from, to) });
  const place = config?.iata ?? "the airport";
  const when = (iso: string) => {
    const t = DateTime.fromISO(iso, { zone: tz });
    return `${t.toFormat("ccc d LLL, HH:mm")}–${t.plus({ minutes: 15 }).toFormat("HH:mm")}`;
  };

  if (q.isLoading) return <Loader2 className="size-5 animate-spin text-ink-3" />;
  if (q.isError) return <p className="text-[13px] text-bad">{(q.error as Error).message}</p>;
  const h = q.data ?? null;

  return (
    <>
      <HowToRead id="history.security">
        <SecurityNotes />
      </HowToRead>
      <p className="mb-4 max-w-3xl text-[14px] text-ink">{securitySentence(h, days, { place, recent: to === today }, when)}</p>
      {h && h.totals.screened != null && (
        <>
          <SecurityCards h={h} tz={tz} when={when} />

          <Section
            title="How long did passengers wait?"
            caption="From the boarding-pass scans: joining the queue to being screened, each passenger once. The open-hours average runs from each day's first screening to its last passenger, so the night does not water it down; a quiet quarter hour inside it counts as no wait."
          >
            <WaitPanel wait={h.wait ?? null} tz={tz} />
          </Section>

          <Section
            title="When was the checkpoint busy?"
            caption="One row per day, one column per clock hour. The darker the cell, the more passengers cleared screening in that hour; every cell is on the same scale, so the same shade is the same number on every row. A row of dots is a day the checkpoint was not read. Under the grid, the average hour."
          >
            <HeatGrid days={h.days} tz={tz} />
          </Section>

          <Section
            title="How early do passengers come through?"
            caption="Each departure's passengers by how long before the scheduled time they cleared screening, in quarter hours, added up over the range. The bars are the share in each quarter hour; the line is the share cleared so far, read on the right-hand scale."
          >
            <ShowUpPanel h={h} />
          </Section>

          {h.airlines.length > 0 && (
            <Section
              title="Whose passengers come earliest?"
              caption="One row per airline, earliest first. Half cleared is how long before departure half its passengers were through; four in five is when the queue for its flights is mostly over. The small picture is the same curve as above, for that airline alone."
            >
              <AirlineLeads airlines={h.airlines} config={config} />
            </Section>
          )}
        </>
      )}
    </>
  );
}

/** The Security tab's How-to-read box. */
export function SecurityNotes() {
  return (
    <>
      <li>Every figure here is counted from the checkpoint&apos;s boarding-pass scans (BPSS2): each passenger once, however many times their pass was scanned.</li>
      <li>The checkpoint is read every ten minutes as the day runs and the counts are kept, so past days stay whole after BPSS2 has moved on.</li>
      <li>
        A departure whose reading BPSS2&apos;s row limit cut short is left out of the show-up curves, as the forecast leaves it out. On weekdays before 13 October 2026 that was
        often the first departures of the day.
      </li>
      <li>BPSS2&apos;s lanes-open figure is not used anywhere, because YLW&apos;s operations people say it does not reflect what is actually open.</li>
      <li>A dash or a dotted row means nothing was recorded, not that nobody came through.</li>
    </>
  );
}

function SecurityCards({ h, tz, when }: { h: SecurityHistory; tz: string; when: (iso: string) => string }) {
  const t = h.totals;
  const perDay = t.screened != null && t.daysMeasured ? Math.round(t.screened / t.daysMeasured) : null;
  const all = h.curves.all;
  return (
    <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      <Card label="Through security" value={num(t.screened)} sub={`over ${t.daysMeasured} ${t.daysMeasured === 1 ? "day" : "days"} counted`} />
      <Card label="A typical day" value={num(perDay)} sub="passengers, averaged over the days counted" />
      <Card
        label="Busiest day"
        value={num(t.busiestDay?.screened)}
        sub={t.busiestDay ? DateTime.fromISO(t.busiestDay.date, { zone: tz }).toFormat("cccc d LLL") : undefined}
      />
      <Card label="Busiest quarter hour" value={num(t.peak?.scans)} sub={t.peak ? `${when(t.peak.at)} · ${num(t.peak.perHour)} an hour` : undefined} />
      <Card label="Half cleared by" value={all.half == null ? "—" : `T-${all.half}`} sub={all.half == null ? "no departure measured" : `${leadWords(all.half)} before departure`} />
      <Card
        label="Departures measured"
        value={num(all.departures)}
        sub={h.setAside ? `${h.setAside} more left out as cut short` : `${num(all.passengers)} passengers in the window`}
      />
    </div>
  );
}

function Card({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-line p-3">
      <div className="text-[11px] tracking-wide text-ink-3 uppercase">{label}</div>
      <div className="tabular text-[20px] font-bold">{value}</div>
      {sub && <div className="text-[11px] text-ink-3">{sub}</div>}
    </div>
  );
}

/** Queue waits over the range: the figures, the shape of a day by hour, the bands, and each day. */
export function WaitPanel({ wait, tz }: { wait: WaitHistory | null; tz: string }) {
  if (!wait) return <p className="text-[12.5px] text-ink-3">Queue waits are not recorded yet. They start with the next BPSS2 read after this release.</p>;
  if (!wait.passengers)
    return (
      <p className="text-[12.5px] text-ink-3">
        {`No queue waits were recorded for these dates. Waits are recorded from ${DateTime.fromISO(wait.since ?? "", { zone: tz }).toFormat("d LLL yyyy")}.`}
      </p>
    );
  const at = (iso: string) => DateTime.fromISO(iso, { zone: tz }).toFormat("ccc d LLL, HH:mm");
  const hourTop = Math.max(1, ...wait.byHour.map((v) => v ?? 0));
  const first = wait.byHour.findIndex((v) => v != null);
  const last = wait.byHour.length - 1 - [...wait.byHour].reverse().findIndex((v) => v != null);
  const hours = first < 0 ? [] : Array.from({ length: last - first + 1 }, (_, i) => first + i);
  const labels = bandLabels(wait.bandEdgesMin);
  const bandTotal = Math.max(1, wait.bands.reduce((a, b) => a + b, 0));

  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <Card label="Last passenger's wait" value={waitWords(wait.last?.sec)} sub={wait.last ? `screened ${at(wait.last.at)}` : undefined} />
        <Card label="Average wait, open hours" value={waitWords(wait.openAvgSec)} sub="first screening to last passenger each day" />
        <Card label="Average per passenger" value={waitWords(wait.avgSec)} sub={`over ${num(wait.passengers)} passengers`} />
        <Card label="Longest wait" value={waitWords(wait.max?.sec)} sub={wait.max ? `in the quarter hour from ${at(wait.max.at)}` : undefined} />
        <Card label={`Over ${wait.targetMin} min`} value={`${wait.overPct}%`} sub={`of passengers waited longer than the ${wait.targetMin}-minute target`} />
      </div>

      <div className="mb-4 grid gap-3 lg:grid-cols-2">
        <div className={card}>
          <div className="mb-2 text-[13px] font-semibold">Average wait by hour of the day</div>
          <div className="flex h-32 items-end gap-1">
            {hours.map((hour) => {
              const v = wait.byHour[hour] ?? null;
              return (
                <div key={hour} className="flex h-full flex-1 flex-col justify-end" title={`${hh(hour)}: ${v == null ? "nobody screened" : waitWords(v)}`}>
                  {v != null && <div className={clsx("rounded-t-[3px]", v / 60 > wait.targetMin ? "bg-warn" : "bg-info")} style={{ height: `${Math.max(2, (v / hourTop) * 100)}%` }} />}
                </div>
              );
            })}
          </div>
          <div className="mt-1 flex gap-1 text-[10px] text-ink-3">
            {hours.map((hour) => (
              <span key={hour} className="flex-1 text-center">
                {hour % 3 === 0 ? String(hour).padStart(2, "0") : ""}
              </span>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-ink-3">{`Amber where the hour's average is over the ${wait.targetMin}-minute target.`}</div>
        </div>

        <div className={card}>
          <div className="mb-2 text-[13px] font-semibold">How long passengers waited</div>
          <div className="flex flex-col gap-1.5">
            {wait.bands.map((n, i) => (
              <div key={labels[i]} className="flex items-center gap-2 text-[12px]">
                <span className="w-20 shrink-0 text-ink-2">{labels[i]}</span>
                <span className="h-3 flex-1 rounded bg-panel-2">
                  <span
                    className={clsx("block h-full rounded", wait.bandEdgesMin[i]! >= wait.targetMin ? "bg-warn" : "bg-info")}
                    style={{ width: `${(n / bandTotal) * 100}%` }}
                  />
                </span>
                <span className="tabular w-24 shrink-0 text-right text-ink-2">{`${num(n)} · ${Math.round((n / bandTotal) * 100)}%`}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="scroll-thin overflow-x-auto">
        <table className="w-full max-w-4xl text-[12.5px]">
          <thead>
            <tr className="border-b border-line">
              <th className={th}>Day</th>
              <th className={th} title="The quarter hours the average covers: the day's first screening to its last passenger">
                Open hours
              </th>
              <th className={th}>Passengers</th>
              <th className={th} title="Over the open hours, a quiet quarter hour counting as no wait">
                Average, open hours
              </th>
              <th className={th}>Average per passenger</th>
              <th className={th}>Longest</th>
              <th className={th}>{`Over ${wait.targetMin} min`}</th>
            </tr>
          </thead>
          <tbody>
            {[...wait.days].reverse().map((d) => (
              <tr key={d.date} className="tabular border-b border-line">
                <td className={clsx(td, "font-semibold")}>{DateTime.fromISO(d.date, { zone: tz }).toFormat("ccc d LLL")}</td>
                <td className={clsx(td, "text-ink-2")}>
                  {d.from && d.to ? `${DateTime.fromISO(d.from, { zone: tz }).toFormat("HH:mm")}–${DateTime.fromISO(d.to, { zone: tz }).toFormat("HH:mm")}` : "—"}
                </td>
                <td className={td}>{num(d.passengers)}</td>
                <td className={td}>{waitWords(d.openAvgSec)}</td>
                <td className={td}>{waitWords(d.avgSec)}</td>
                <td className={td} title={d.maxAt ? `in the quarter hour from ${DateTime.fromISO(d.maxAt, { zone: tz }).toFormat("HH:mm")}` : undefined}>
                  {waitWords(d.maxSec)}
                </td>
                <td className={clsx(td, d.overPct > 10 ? "text-warn" : undefined)}>{`${d.overPct}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/**
 * Days down, clock hours across, the busy cells dark. A grid rather than a line because
 * the question is two at once: which days, and which hours of them.
 */
export function HeatGrid({ days, tz }: { days: SecurityDay[]; tz: string }) {
  const [at, setAt] = useState<{ date: string; hour: number } | null>(null);
  const shape = shapeHeat(days);
  if (!shape.hours.length) return <p className="text-[12.5px] text-ink-3">Nothing was recorded at the checkpoint for these dates.</p>;
  const cols = `5.5rem repeat(${shape.hours.length}, minmax(0, 1fr)) 3.5rem`;
  const hovered = at ? shape.rows.find((r) => r.date === at.date)?.cells.find((c) => c.hour === at.hour) : null;
  const dayLabel = (date: string) => DateTime.fromISO(date, { zone: tz }).toFormat("ccc d LLL");

  return (
    <div className={clsx(card, "mb-4")} onMouseLeave={() => setAt(null)}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[13px] font-semibold">Passengers through screening, by hour</div>
        <div className="tabular text-[11.5px] text-ink-3" aria-live="polite">
          {at && hovered
            ? `${dayLabel(at.date)}, ${hh(at.hour)}–${hh((at.hour + 1) % 24)}: ${hovered.v == null ? "not read" : `${hovered.v.toLocaleString()} passengers`}`
            : "Hover a cell for its figure"}
        </div>
      </div>

      <div className="scroll-thin overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="grid gap-px text-[10px] text-ink-3" style={{ gridTemplateColumns: cols }}>
            <span />
            {shape.hours.map((hour) => (
              <span key={hour} className="text-center">
                {hour % 3 === 0 ? String(hour).padStart(2, "0") : ""}
              </span>
            ))}
            <span className="text-right">day</span>
          </div>

          {shape.rows.map((r) => (
            <div key={r.date} className="mt-px grid gap-px" style={{ gridTemplateColumns: cols }}>
              <span className="truncate pr-1 text-[11px] leading-[18px] text-ink-2">{dayLabel(r.date)}</span>
              {r.cells.map((c) => (
                <span
                  key={c.hour}
                  data-cell
                  className={clsx(
                    "h-[18px] rounded-[3px]",
                    c.v == null && "bg-[radial-gradient(circle,var(--color-line)_1px,transparent_1.5px)] bg-[length:6px_6px]",
                    at?.date === r.date && at.hour === c.hour && "ring-2 ring-ink",
                  )}
                  style={{ background: heatColor(c) }}
                  title={`${dayLabel(r.date)}, ${hh(c.hour)}: ${c.v == null ? "not read" : `${c.v.toLocaleString()} passengers`}`}
                  onMouseEnter={() => setAt({ date: r.date, hour: c.hour })}
                />
              ))}
              <span className="tabular text-right text-[11px] leading-[18px] text-ink-2">{r.total == null ? "—" : r.total.toLocaleString()}</span>
            </div>
          ))}

          {/* The average hour, as bars in the same columns, so the grid's shape is said once more in a line. */}
          <div className="mt-3 grid items-end gap-px border-t border-line pt-2" style={{ gridTemplateColumns: cols }}>
            <span className="text-[11px] text-ink-3">Average hour</span>
            {shape.typical.map((t) => (
              <span key={t.hour} className="flex h-10 items-end" title={`${hh(t.hour)}: ${Math.round(t.avg).toLocaleString()} passengers on average`}>
                <span className="block w-full rounded-t-[3px] bg-info" style={{ height: `${Math.max(t.avg > 0 ? 4 : 0, t.level * 100)}%` }} />
              </span>
            ))}
            <span />
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-ink-3">
        <span>fewer</span>
        {[0.1, 0.3, 0.5, 0.7, 0.9].map((level) => (
          <span key={level} className="inline-block h-2.5 w-5 rounded-sm" style={{ background: heatColor({ v: 1, level }) }} />
        ))}
        <span>{`more · the darkest is ${shape.max.toLocaleString()} passengers in an hour`}</span>
        <span className="ml-2 inline-block h-2.5 w-5 rounded-sm bg-[radial-gradient(circle,var(--color-line)_1px,transparent_1.5px)] bg-[length:6px_6px]" /> not read
      </div>
    </div>
  );
}

const SECTORS = [
  { key: "all", label: "Every flight" },
  { key: "D", label: "Domestic" },
  { key: "T", label: "Transborder" },
  { key: "I", label: "International" },
] as const;

/** The show-up curve, with a switch between every flight and each sector that measured any. */
function ShowUpPanel({ h }: { h: SecurityHistory }) {
  const [sector, setSector] = useState<(typeof SECTORS)[number]["key"]>("all");
  const offered = SECTORS.filter((s) => s.key === "all" || h.curves[s.key].departures > 0);
  const curve = h.curves[sector];
  return (
    <div className={card}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex overflow-hidden rounded-md border border-line text-[12px]">
          {offered.map((s) => (
            <button
              key={s.key}
              className={clsx("px-2 py-1", sector === s.key ? "bg-panel-2 font-semibold" : "text-ink-2 hover:text-ink")}
              aria-pressed={sector === s.key}
              onClick={() => setSector(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="text-[11.5px] text-ink-3">
          {curve.departures ? `${num(curve.departures)} departures, ${num(curve.passengers)} passengers` : "No departure measured"}
        </div>
      </div>
      <ShowUpChart curve={curve} />
    </div>
  );
}

/** Quarter-hour bars from T-240 to departure, the running total over them, and where half and four in five had cleared. */
export function ShowUpChart({ curve }: { curve: ShowUpCurve }) {
  const [at, setAt] = useState<number | null>(null);
  if (!curve.departures) return <p className="py-2 text-[12.5px] text-ink-3">No departure in the range has a show-up reading.</p>;
  const shape = shapeCurve(curve);
  const hovered = at == null ? null : shape.bars[at];
  const cumAt = at == null ? null : 100 - shape.cumulative[at + 1]!.y;
  const inset = shape.bars[0] ? shape.bars[0].w * 0.12 : 0;

  return (
    <div onMouseLeave={() => setAt(null)}>
      <div className="mb-2 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
        <span>
          Half had cleared by <b className="tabular">{`T-${curve.half}`}</b> <span className="text-ink-3">{`(${leadWords(curve.half)} before)`}</span>
        </span>
        <span>
          Four in five by <b className="tabular">{`T-${curve.most}`}</b> <span className="text-ink-3">{`(${leadWords(curve.most)} before)`}</span>
        </span>
      </div>

      <div className="flex gap-2">
        <div className="relative h-44 w-8 shrink-0 text-[10px] text-ink-3">
          {[0.5, 1].map((f) => (
            <div key={f} className="absolute right-0 -translate-y-1/2" style={{ bottom: `${f * 100}%` }}>
              {Math.round(shape.top * f)}%
            </div>
          ))}
        </div>

        <div className="relative flex-1">
          <div className="relative h-44 border-b border-line">
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
              {[25, 50, 75].map((y) => (
                <line key={y} x1={0} x2={100} y1={y} y2={y} className="stroke-line/60" strokeWidth={1} vectorEffect="non-scaling-stroke" />
              ))}
              {shape.bars.map((b, i) => (
                <rect key={i} x={b.x + inset} y={100 - b.h} width={b.w - 2 * inset} height={b.h} className={clsx("fill-info", at === i ? "opacity-100" : "opacity-75")} />
              ))}
              {shape.halfX != null && (
                <line x1={shape.halfX} x2={shape.halfX} y1={0} y2={100} className="stroke-warn" strokeWidth={1.5} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
              )}
              {shape.mostX != null && (
                <line x1={shape.mostX} x2={shape.mostX} y1={0} y2={100} className="stroke-ok" strokeWidth={1.5} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
              )}
              <polyline
                points={shape.cumulative.map((p) => `${p.x},${p.y}`).join(" ")}
                fill="none"
                className="stroke-ink"
                strokeWidth={2}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            <div className="absolute inset-0 flex">
              {shape.bars.map((b, i) => (
                <button
                  key={i}
                  className="h-full flex-1 cursor-default"
                  onMouseEnter={() => setAt(i)}
                  onFocus={() => setAt(i)}
                  aria-label={`T-${b.minsBefore} to T-${b.minsBefore - 15}: ${b.share.toFixed(1)}% of passengers`}
                />
              ))}
            </div>
            {hovered && (
              <div
                className="pointer-events-none absolute top-0 z-20 w-48 rounded-lg border border-line bg-panel p-2 text-[11.5px] shadow-lg"
                style={{ left: `min(max(0px, calc(${hovered.x + hovered.w / 2}% - 96px)), calc(100% - 192px))` }}
              >
                <div className="font-semibold">{`T-${hovered.minsBefore} to ${hovered.minsBefore === 15 ? "departure" : `T-${hovered.minsBefore - 15}`}`}</div>
                <div className="flex justify-between text-ink-2">
                  <span>In this quarter hour</span>
                  <span className="tabular text-ink">{hovered.share.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between text-ink-2">
                  <span>Cleared by its end</span>
                  <span className="tabular text-ink">{cumAt!.toFixed(0)}%</span>
                </div>
              </div>
            )}
          </div>
          <div className="relative mt-1 h-4 text-[10px] text-ink-3">
            {[240, 180, 120, 60, 0].map((m) => (
              <span key={m} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${((240 - m) / 240) * 100}%` }}>
                {m === 0 ? "departs" : `T-${m}`}
              </span>
            ))}
          </div>
        </div>

        <div className="relative h-44 w-9 shrink-0 text-[10px] text-ink-3">
          {[0, 50, 100].map((v) => (
            <div key={v} className="absolute left-0 -translate-y-1/2" style={{ bottom: `${v}%` }}>
              {v}%
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-ink-3">
        <span className="flex items-center gap-1">
          <span className="inline-block size-2.5 rounded-sm bg-info" /> share in each quarter hour
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 rounded bg-ink" /> cleared so far
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-0 w-4 border-t-2 border-dashed border-warn" /> half
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-0 w-4 border-t-2 border-dashed border-ok" /> four in five
        </span>
      </div>
    </div>
  );
}

/** A curve the size of a word, for a table row: the same bars, no axes. */
function Spark({ curve }: { curve: ShowUpCurve }) {
  const shape = shapeCurve(curve);
  return (
    <svg className="h-5 w-28" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
      {shape.bars.map((b, i) => (
        <rect key={i} x={b.x + b.w * 0.1} y={100 - b.h} width={b.w * 0.8} height={b.h} className="fill-info" />
      ))}
      {shape.halfX != null && <line x1={shape.halfX} x2={shape.halfX} y1={0} y2={100} className="stroke-warn" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />}
    </svg>
  );
}

export function AirlineLeads({ airlines, config }: { airlines: SecurityHistory["airlines"]; config: AirportConfig | undefined }) {
  const longest = Math.max(1, ...airlines.map((a) => a.curve.half ?? 0));
  return (
    <div className="scroll-thin overflow-x-auto">
      <table className="w-full max-w-4xl text-[12.5px]">
        <thead>
          <tr className="border-b border-line">
            <th className={th}>Airline</th>
            <th className={th} title="Departures with a show-up reading in the range">
              Departures
            </th>
            <th className={th} title="Passengers counted inside the four hours before departure">
              Passengers
            </th>
            <th className={th} title="How long before departure half its passengers had cleared screening">
              Half cleared
            </th>
            <th className={th} title="How long before departure four in five had cleared">
              Four in five
            </th>
            <th className={th} title="Its passengers by quarter hour, T-240 on the left, departure on the right; the amber mark is the half">
              When they come
            </th>
          </tr>
        </thead>
        <tbody>
          {airlines.map((a) => (
            <tr key={a.airline} className="border-b border-line">
              <td className={td}>
                <AirlineMark code={a.airline} config={config} withName />
              </td>
              <td className={clsx(td, "tabular")}>{num(a.curve.departures)}</td>
              <td className={clsx(td, "tabular")}>{num(a.curve.passengers)}</td>
              <td className={td}>
                <span className="flex items-center gap-1.5">
                  <span className="tabular w-16 shrink-0">{a.curve.half == null ? "—" : `T-${a.curve.half}`}</span>
                  {a.curve.half != null && (
                    <span className="h-1.5 w-20 shrink-0 rounded-full bg-panel-2">
                      <span className="block h-full rounded-full bg-warn" style={{ width: `${(a.curve.half / longest) * 100}%` }} />
                    </span>
                  )}
                </span>
              </td>
              <td className={clsx(td, "tabular")}>{a.curve.most == null ? "—" : `T-${a.curve.most}`}</td>
              <td className={td}>
                <Spark curve={a.curve} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
